import { Search } from "@komponentkassen/svv-icons";
import classNames from "classnames";
import React, { ReactNode, useId, useState } from "react";
import { SVVFormError } from "./FormError";
import { SVVLabel } from "./Label";
import { SearchFieldProps } from "./typer";
import { Description } from "./Description";
import { SVVConditionalWrapper } from "./ConditionalWrapper";

export function SVVSearchField({
  id,
  label,
  onSearch,
  className,
  errorMessage,
  inputSize = "medium",
  placeholder,
  required,
  popoverText,
  popoverTitle,
  searchText,
  buttonText = "Søk",
  description,
  wrapInFormTag = true,
  ...rest
}: SearchFieldProps) {
  const [errorMessageId] = useState(`feilmelding-${useId()}`);
  const hasErrorMessage = !!errorMessage;
  const [text, setText] = useState(searchText || "");

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSearch(text);
  };

  return (
    <SVVConditionalWrapper
      condition={wrapInFormTag}
      renderWrapper={(children: ReactNode) => (
        <form onSubmit={handleSubmit} noValidate>
          {children}
        </form>
      )}
    >
      <div className={classNames("svv-search-field", className)}>
        {label && (
          <SVVLabel
            htmlFor={id}
            required={required}
            popoverText={popoverText}
            popoverTitle={popoverTitle}
          >
            {label}
          </SVVLabel>
        )}
        {description && <Description content={description} />}
        <div className="svv-search-field__container">
          <input
            id={id}
            className={classNames("svv-form-input__text", {
              "svv-form-input__text--error": hasErrorMessage,
              "input--x-small": inputSize === "xsmall",
              "input--small": inputSize === "small",
              "input--medium": inputSize === "medium",
              "input--large": inputSize === "large",
            })}
            placeholder={placeholder}
            aria-invalid={hasErrorMessage}
            aria-describedby={hasErrorMessage ? errorMessageId : undefined}
            required={required}
            type="search"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setText(e.target.value)}
            value={text}
            {...rest}
          />
          <button className="svv-button" type="submit">
            <Search />
            <span className="svv-search-field__text">{buttonText}</span>
          </button>
        </div>
        <div aria-live="assertive">
          {errorMessage && (
            <SVVFormError id={errorMessageId}>
              <p>{errorMessage}</p>
            </SVVFormError>
          )}
        </div>
      </div>
    </SVVConditionalWrapper>
  );
}
